/**
 * Social Media Audit engine: computes Audit Score and Top-5 findings from a snapshot.
 * Used by the Social Media Audit tool (client and optionally server for cached results).
 */

export interface SnapshotPost {
  id: string;
  timestamp: string;
  likes: number;
  comments: number;
  text: string;
  hasCTA?: boolean;
  hasAlt?: boolean;
}

export interface AuditSnapshot {
  handle: string;
  platform: string;
  followers: number;
  postsPerWeek: number;
  recentPosts: SnapshotPost[];
}

export interface AuditFinding {
  id: string;
  title: string;
  severity: 'low' | 'medium' | 'high';
  details: string;
  /** Example post IDs or snippets as evidence */
  evidence: string[];
  /** Actionable remediation step */
  remediation: string;
  /** Impact: 'high' | 'medium' | 'low' */
  impact: string;
  /** Difficulty: 'easy' | 'medium' | 'hard' */
  difficulty: string;
  /** Try Fix link: 'calculator' | 'hashtag-research' | null */
  tryFixTool: 'calculator' | 'hashtag-research' | null;
  tryFixQuery?: string;
}

export interface AuditResult {
  score: number;
  findings: AuditFinding[];
  metrics: {
    engagementRate: number;
    avgEngagement: number;
    followers: number;
    postsPerWeek: number;
  };
}

const MAX_FINDINGS = 5;

export function computeAudit(snapshot: AuditSnapshot): AuditResult {
  const { followers, postsPerWeek, recentPosts } = snapshot;
  // Sanitize recentPosts to tolerate null/undefined/malformed entries
  const sanitizedPosts = (recentPosts || [])
    .filter((p): p is SnapshotPost => !!p && typeof p === 'object')
    .map((p) => ({
      id: p.id ?? '',
      timestamp: p.timestamp ?? '',
      likes: typeof p.likes === 'number' ? p.likes : Number(p.likes) || 0,
      comments: typeof p.comments === 'number' ? p.comments : Number(p.comments) || 0,
      text: typeof p.text === 'string' ? p.text : String(p.text || ''),
      hasCTA: !!p.hasCTA,
      hasAlt: !!p.hasAlt,
    }));
  const postCount = Math.max(1, sanitizedPosts.length);
  const avgEngagement =
    sanitizedPosts.reduce((s, p) => s + (p.likes + (p.comments || 0)), 0) / postCount;
  const engagementRate = followers > 0 ? avgEngagement / followers : 0;

  let score = 100;
  const findings: AuditFinding[] = [];

  // Low engagement
  if (engagementRate < 0.01) {
    score -= 30;
    const examples = sanitizedPosts.slice(0, 2).map((p) => `Post "${(p.text || '').slice(0, 40)}..." — ${p.likes} likes`);
    findings.push({
      id: 'low_engagement',
      title: 'Low engagement rate',
      severity: 'high',
      details: `Engagement rate ${(engagementRate * 100).toFixed(2)}% is below benchmark (aim for 1%+).`,
      evidence: examples,
      remediation: 'Post more consistently, use strong CTAs, and align content with audience interests. Use the Social Media Calculator to model impact.',
      impact: 'high',
      difficulty: 'medium',
      tryFixTool: 'calculator',
      tryFixQuery: `followers=${followers}&postsPerWeek=${postsPerWeek}`,
    });
  }

  // Low posting frequency
  if (postsPerWeek < 2) {
    score -= 20;
    findings.push({
      id: 'low_frequency',
      title: 'Low posting frequency',
      severity: 'medium',
      details: `Posting ${postsPerWeek} times/week; try 3–5 for better reach.`,
      evidence: [],
      remediation: 'Increase posting cadence to 3–5 times per week. Schedule content in advance.',
      impact: 'medium',
      difficulty: 'easy',
      tryFixTool: 'calculator',
      tryFixQuery: `followers=${followers}&postsPerWeek=4`,
    });
  }

  // Missing CTAs
  const postsWithoutCTA = sanitizedPosts.filter((p) => !p.hasCTA).length;
  if (postsWithoutCTA > sanitizedPosts.length / 2) {
    score -= 15;
    const examples = sanitizedPosts.filter((p) => !p.hasCTA).slice(0, 2).map((p) => (p.text || '').slice(0, 60) + '...');
    findings.push({
      id: 'missing_cta',
      title: 'Missing calls-to-action',
      severity: 'medium',
      details: `Most recent posts do not include clear CTAs (e.g. "Link in bio", "Comment below").`,
      evidence: examples,
      remediation: 'Add one clear CTA per post (e.g. "Follow for more", "Tap the link", "Comment your answer").',
      impact: 'high',
      difficulty: 'easy',
      tryFixTool: null,
    });
  }

  // Missing alt text
  const postsMissingAlt = sanitizedPosts.filter((p) => !p.hasAlt).length;
  if (postsMissingAlt > 2) {
    score -= 10;
    const examples = sanitizedPosts.filter((p) => !p.hasAlt).slice(0, 2).map((p) => `Post ${p.id}`);
    findings.push({
      id: 'missing_alt',
      title: 'Missing image alt text',
      severity: 'low',
      details: `${postsMissingAlt} recent posts missing alt text; hurts accessibility and SEO.`,
      evidence: examples,
      remediation: 'Add descriptive alt text to every image (what’s in the image, not just "image").',
      impact: 'medium',
      difficulty: 'easy',
      tryFixTool: null,
    });
  }

  // Inconsistent branding (simple heuristic: very few posts mention handle or brand terms)
  const withHandle = sanitizedPosts.filter((p) => (p.text || '').toLowerCase().includes(snapshot.handle.toLowerCase())).length;
  if (sanitizedPosts.length >= 3 && withHandle < sanitizedPosts.length / 3) {
    score -= 10;
    findings.push({
      id: 'inconsistent_branding',
      title: 'Inconsistent branding',
      severity: 'low',
      details: 'Few recent posts reinforce your handle or brand; consistency helps recognition.',
      evidence: [],
      remediation: 'Mention your handle or brand naturally in captions where relevant. Keep a consistent voice.',
      impact: 'low',
      difficulty: 'easy',
      tryFixTool: 'hashtag-research',
      tryFixQuery: snapshot.handle,
    });
  }

  if (score < 0) score = 0;

  // Return Top-5 by severity order (high first)
  const severityOrder = { high: 0, medium: 1, low: 2 };
  const topFindings = [...findings]
    .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
    .slice(0, MAX_FINDINGS);

  return {
    score,
    findings: topFindings,
    metrics: { engagementRate, avgEngagement, followers, postsPerWeek },
  };
}
